package com.bikompanion.ui.home

import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import org.maplibre.spatialk.geojson.Position
import com.bikompanion.domain.BikeStation
import com.bikompanion.ui.composables.STATION_NAMES_VISIBLE_ZOOM
import kotlin.math.PI
import kotlin.math.atan
import kotlin.math.cos
import kotlin.math.exp
import kotlin.math.ln
import kotlin.math.pow
import kotlin.math.sin
import kotlin.math.tan

// MapLibre draws the world 512 dp wide at zoom 0 and doubles it with every level.
private const val WorldSizeAtZoomZero = 512.0

/**
 * The point that, with the camera centred on it, puts this position [left] to the left of and [up]
 * above the screen's centre (negative values go the other way).
 *
 * Lets a station be framed in the part of the map that the docked panel and the navigation bar
 * leave free instead of under them. Done on the centre rather than with camera padding because
 * padding is sticky: it would keep shifting every later "centre on the rider" and need undoing,
 * with a jump, when the panel closed. Walks in Web Mercator pixels so the shift is a constant
 * on-screen distance whatever the latitude, and along the camera's [bearing] so it stays
 * screen-aligned on a rotated map.
 */
internal fun Position.centreForScreenOffset(
    left: Dp,
    up: Dp = 0.dp,
    zoom: Double,
    bearing: Double,
): Position {
    if (left == 0.dp && up == 0.dp) return this
    val world = WorldSizeAtZoomZero * 2.0.pow(zoom)
    val bearingRadians = bearing * PI / 180.0
    val x = (longitude + 180.0) / 360.0 * world
    val y = (0.5 - ln(tan(PI / 4.0 + latitude * PI / 360.0)) / (2.0 * PI)) * world
    // In map pixels (y grows southward) screen-right is (cos b, sin b) and screen-down is
    // (-sin b, cos b): east and south at north-up, south and west once the map has been turned a
    // quarter clockwise. The camera moves right and down so the station reads left and up.
    val shiftedX = x + left.value * cos(bearingRadians) - up.value * sin(bearingRadians)
    val shiftedY = y + left.value * sin(bearingRadians) + up.value * cos(bearingRadians)
    return Position(
        longitude = shiftedX / world * 360.0 - 180.0,
        latitude = (2.0 * atan(exp((0.5 - shiftedY / world) * 2.0 * PI)) - PI / 2.0) * 180.0 / PI,
    )
}

internal fun resolveStationFocus(
    target: StationFocusTarget,
    stations: List<BikeStation>,
    currentZoom: Double,
): StationFocusResolution? {
    val station = stations.firstOrNull { it.networkId == target.networkId && it.id == target.stationId }
        ?: return null
    return StationFocusResolution(
        target = target,
        station = station,
        zoom = maxOf(currentZoom, STATION_NAMES_VISIBLE_ZOOM),
    )
}
